// Projection 탭 — 바닐라 dcf.html #tab-projection / dcf.js 의
// renderHist·readHist·addHistRow·removeHistRow(과거 손익),
// fillDrivers·readForm(추정 드라이버), renderFa·readFa·addFaRow·delFaRow·
// onDepMethodChange(자산등록부), fillNwcCells·toggleNwcInputs(NWC),
// renderResult 의 forecast-out 부분, renderFaSchedule 이식.
//
// 입력 방식은 AssumptionsTab 과 동일: 비제어 입력 + blur 커밋(key={value} 재초기화).
// 과거 실적 표는 엑셀 TSV 붙여넣기(onPaste) 지원 — lib/hist.ts (바닐라 handleHistPaste).
// 에이전트 dcf_patch 가 건드린 드라이버 행은 cell-flash 로 1.5초 하이라이트된다.
import { useEffect, useRef, useState } from "react";
import { fmt, parseNum } from "../../lib/format";
import { applyHistPaste, normalizeHistDate, parseHistClipboard } from "../../lib/hist";
import { unitMoney, useT, yearCol } from "../../lib/i18n";
import { Button } from "../../ui/Button";
import { pathsInclude, scenArr, useDcf, withScenArr } from "../../stores/dcf";
import type {
  DcfCapexItem,
  DcfFixedAsset,
  DcfHistBsRow,
  DcfForecastDrivers,
  DcfHistRow,
  ScenarioArray,
} from "../../types/dcf";

const FA_CATS: Array<[string, string]> = [
  ["tangible", "opt.fa_tangible"],
  ["intangible", "opt.fa_intangible"],
  ["right_of_use", "opt.fa_rou"],
];

function faCatKey(c?: string): string {
  return c === "intangible"
    ? "fa.intangible_short"
    : c === "right_of_use"
      ? "fa.rou_short"
      : "fa.tangible_short";
}

/** 표 셀 입력 — 바닐라 .proj-input 과 동일 스타일, blur 시 커밋.
 *  flash: 에이전트 patch 대상 → cell-flash (key 에 flashSeq 를 섞어 값이 같아도 재발화)
 *  onPaste: 과거 실적 표의 TSV 붙여넣기 훅 */
function Cell({
  value,
  onCommit,
  disabled,
  left,
  flash,
  flashSeq,
  onPaste,
}: {
  value: string;
  onCommit: (v: string) => void;
  disabled?: boolean;
  left?: boolean;
  flash?: boolean;
  flashSeq?: number;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <input
      key={flash ? `${value}::f${flashSeq}` : value}
      className={`proj-input${flash ? " cell-flash" : ""}`}
      defaultValue={value}
      disabled={disabled}
      style={left ? { textAlign: "left" } : undefined}
      onPaste={onPaste}
      onBlur={(e) => {
        if (e.target.value !== value) onCommit(e.target.value);
      }}
    />
  );
}

const YEARS_5 = [0, 1, 2, 3, 4] as const;

export default function ProjectionTab() {
  const { lang, t, tf } = useT();
  const current = useDcf((s) => s.current);
  const setPayloadField = useDcf((s) => s.setPayloadField);
  const recalc = useDcf((s) => s.recalc);
  // 에이전트 patch 하이라이트 — 경로가 포함된 입력에 cell-flash (바닐라 fillDriverCellsFromPatch)
  const lastPatchPaths = useDcf((s) => s.lastPatchPaths);
  const patchSeq = useDcf((s) => s.patchSeq);

  const p = current?.payload;
  const fd: DcfForecastDrivers = p?.forecast_drivers || {};
  // 매출 입력 방식 — 초기값: 저장된 스위치 우선, 없으면 payload 에서 유추
  const [revMode, setRevMode] = useState<"amounts" | "growth">(() => {
    const fd0: DcfForecastDrivers = p?.forecast_drivers || {};
    if (fd0.revenue_input_method === "amounts" || fd0.revenue_input_method === "growth")
      return fd0.revenue_input_method;
    const hasAmounts = !!(fd0.revenue_plan_amounts && fd0.revenue_plan_amounts.length);
    return hasAmounts ? "amounts" : fd0.revenue_growth ? "growth" : "amounts";
  });
  // patch 가 보낸 매출 형식(금액/성장률)에 맞춰 UI 모드 자동 전환 —
  // 바닐라 fillDriverCellsFromPatch (rev-1..5 셀은 두 모드가 공유하므로 필수)
  useEffect(() => {
    if (!patchSeq) return;
    const paths = useDcf.getState().lastPatchPaths;
    const nowFd = useDcf.getState().current?.payload.forecast_drivers || {};
    if (
      pathsInclude(paths, "forecast_drivers.revenue_plan_amounts") &&
      nowFd.revenue_plan_amounts != null
    )
      setRevMode("amounts");
    else if (pathsInclude(paths, "forecast_drivers.revenue_growth"))
      setRevMode("growth");
  }, [patchSeq]);
  // (훅 규칙) 아래 undo 훅 묶음은 얼리 리턴보다 앞에 있어야 한다 — 조건부 훅은
  // current/p 가 생기는 순간 훅 순서가 바뀌어 크래시한다. 콜백 안에서만 뒷부분의
  // 함수(syncCapexTotals·setPayloadField)를 쓰므로 앞으로 옮겨도 동작은 같다.
  // ── 삭제 되돌리기 — x 는 확인 없이 지우므로 실수 복구 수단이 있어야 한다 ──
  // 지우기 전의 배열 전체를 스택에 쌓고, Ctrl+Z 또는 버튼으로 최근 것부터 되돌린다.
  // 값 편집은 셀에서 브라우저 자체 undo 가 동작하므로 여기서는 삭제만 다룬다.
  const undoRef = useRef<Array<{ field: string; prev: unknown; label: string }>>([]);
  const [undoCount, setUndoCount] = useState(0);
  const [lastDeleted, setLastDeleted] = useState("");
  const pushUndo = (field: string, prev: unknown, label: string) => {
    undoRef.current.push({ field, prev, label });
    if (undoRef.current.length > 20) undoRef.current.shift();
    setUndoCount(undoRef.current.length);
    setLastDeleted(label);
  };
  const undoLast = () => {
    const u = undoRef.current.pop();
    setUndoCount(undoRef.current.length);
    setLastDeleted(undoRef.current[undoRef.current.length - 1]?.label || "");
    if (!u) return;
    // capex 는 합계 드라이버를 함께 되돌려야 한다 — 삭제가 그 둘을 같이 바꿨다
    if (u.field === "capex_items") syncCapexTotals(u.prev as DcfCapexItem[]);
    else setPayloadField(u.field, u.prev);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z" || e.shiftKey) return;
      const t = e.target as HTMLElement;
      // 입력란 안에서는 브라우저의 글자 undo 가 우선이다
      if (t.closest("input, textarea, [contenteditable=true]")) return;
      if (!undoRef.current.length) return;
      e.preventDefault();
      undoLast();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!current || !p) return null;

  const flashOf = (path: string) => pathsInclude(lastPatchPaths, path);
  const revFlash =
    flashOf("forecast_drivers.revenue_plan_amounts") ||
    flashOf("forecast_drivers.revenue_growth");

  const scen = fd.scenario_choice || "business_plan";
  const years = Math.min(5, Math.max(1, p.forecast_full_years || 5));
  const r = current.result;

  // ── 과거 손익 (Historical P&L) ──
  const hist: DcfHistRow[] = p.historical_pl || [];
  const commitHist = (i: number, key: keyof DcfHistRow, raw: string) => {
    const rows = hist.map((row) => ({ ...row }));
    while (rows.length <= i)
      rows.push({ year_end: "", revenue: 0, cogs: 0, sga: 0, depreciation: 0 });
    if (key === "year_end") rows[i].year_end = normalizeHistDate(raw);
    else rows[i][key] = parseNum(raw);
    setPayloadField("historical_pl", rows);
  };
  // 엑셀 TSV 붙여넣기 — 바닐라 handleHistPaste. 단일 값이면 기본 붙여넣기 유지,
  // 여러 셀이면 (i, col) 기준으로 채우고 부족한 행은 자동 추가된다.
  // 화면은 transpose(컬럼=연도) — 붙여넣은 행렬을 뒤집어 기존 적용 함수에 넘긴다.
  // (fieldCol, yearIdx) 에서 시작: 붙여넣기 행=계정, 열=연도.
  const onHistPasteT =
    (fieldCol: number, yearIdx: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData("text") || "";
      const matrix = parseHistClipboard(text);
      if (!matrix) return;
      e.preventDefault();
      const flipped: string[][] = [];
      matrix.forEach((cells, ri) =>
        cells.forEach((v, ci) => {
          (flipped[ci] ||= [])[ri] = v;
        }),
      );
      setPayloadField("historical_pl", applyHistPaste(hist, yearIdx, fieldCol, flipped));
    };
  const addHistRow = () =>
    setPayloadField("historical_pl", [
      ...hist.map((row) => ({ ...row })),
      { year_end: "", revenue: 0, cogs: 0, sga: 0, depreciation: 0 },
    ]);

  const removeHistRow = (i: number) => {
    if (hist.length <= 1) return; // 바닐라 removeHistRow — 최소 1행 유지
    pushUndo("historical_pl", hist.map((row) => ({ ...row })), hist[i]?.year_end || t("h.historical_pl"));
    setPayloadField(
      "historical_pl",
      hist.filter((_, idx) => idx !== i).map((row) => ({ ...row })),
    );
  };

  // ── 과거 운전자본 (Historical BS) ──
  // 직전 순운전자본은 historical_bs 마지막 연도에서 나오는데(forecast.py prev_nwc)
  // 화면에 입력란이 없어 샘플값(2,590)이 그대로 쓰이고 있었다. 운전자본은 첫해
  // FCF 에 그대로 꽂히므로 평가액을 흔든다.
  const BS_FIELDS: Array<[string, keyof DcfHistBsRow]> = [
    ["row.bs_ar", "accounts_receivable"],
    ["row.bs_inv", "inventory"],
    ["row.bs_oca", "other_current_assets"],
    ["row.bs_ap", "accounts_payable"],
    ["row.bs_ocl", "other_current_liabilities"],
  ];
  const histBs: DcfHistBsRow[] = (p.historical_bs as DcfHistBsRow[]) || [];
  const emptyBs = (): DcfHistBsRow => ({
    year_end: "", accounts_receivable: 0, inventory: 0, other_current_assets: 0,
    accounts_payable: 0, other_current_liabilities: 0,
  });
  const bsNwc = (row: DcfHistBsRow): number =>
    ((row.accounts_receivable ?? 0) + (row.inventory ?? 0) + (row.other_current_assets ?? 0))
    - ((row.accounts_payable ?? 0) + (row.other_current_liabilities ?? 0));
  const commitBs = (i: number, key: keyof DcfHistBsRow, raw: string) => {
    const rows = histBs.map((r) => ({ ...r }));
    while (rows.length <= i) rows.push(emptyBs());
    if (key === "year_end") rows[i].year_end = normalizeHistDate(raw);
    else rows[i][key] = parseNum(raw);
    setPayloadField("historical_bs", rows);
  };
  const addBsRow = () =>
    setPayloadField("historical_bs", [...histBs.map((r) => ({ ...r })), emptyBs()]);
  const delBsRow = (i: number) => {
    pushUndo("historical_bs", histBs.map((r) => ({ ...r })), histBs[i]?.year_end || t("h.historical_bs"));
    setPayloadField("historical_bs", histBs.filter((_, k) => k !== i).map((r) => ({ ...r })));
  };
  /** 과거 손익에 적어 둔 연도를 그대로 가져온다 — 연도를 두 번 치게 하지 않는다. */
  const pullBsYears = () => {
    const years_ = hist.map((h) => h.year_end).filter(Boolean) as string[];
    if (!years_.length) return;
    const byYear = new Map(histBs.map((r) => [r.year_end, r]));
    setPayloadField("historical_bs",
      years_.map((y) => ({ ...(byYear.get(y) || emptyBs()), year_end: y })));
  };
  // 값이 하나라도 있는 행이 없으면 직전 NWC 가 0 이 된다 — 조용히 두면 안 된다
  const bsMissing = !histBs.some((r) => BS_FIELDS.some(([, f]) => (r[f] as number) > 0));

  // ── 추정 드라이버 ──
  const rev =
    revMode === "growth"
      ? scenArr(fd, "revenue_growth", scen)
      : fd.revenue_plan_amounts || [];
  const commitRev = (i: number, raw: string) => {
    const n = parseNum(raw);
    const next: DcfForecastDrivers = { ...fd, scenario_choice: scen };
    if (revMode === "amounts") {
      const arr = [...(fd.revenue_plan_amounts || [])];
      while (arr.length <= i) arr.push(0);
      arr[i] = n;
      next.revenue_plan_amounts = arr;
      next.revenue_input_method = "amounts";
    } else {
      const arr = [...rev];
      while (arr.length <= i) arr.push(0);
      arr[i] = n / 100;
      next.revenue_growth = withScenArr(fd.revenue_growth, scen, arr);
      next.revenue_input_method = "growth";   // 절대금액은 지우지 않는다 — 전환 시 복원
    }
    setPayloadField("forecast_drivers", next);
  };
  const revCell = (i: number): string => {
    const v = rev[i];
    if (v == null) return "";
    return revMode === "growth"
      ? (v * 100).toFixed(1)
      : Math.round(v).toLocaleString("ko-KR");
  };

  type DriverKey = "cogs_ratio" | "sga_drivers" | "depreciation_drivers" | "capex_drivers";
  // 계정별 단위(%/금액) — 엔진의 method 플래그가 진실이다. 배열은 하나뿐이고
  // 해석만 바뀌므로, 단위를 전환해도 숫자는 지워지지 않는다.
  const modeOf = (key: DriverKey): "pct" | "amt" => {
    if (key === "cogs_ratio") return fd.cogs_method === "absolute" ? "amt" : "pct";
    if (key === "sga_drivers") return fd.sga_method === "absolute" ? "amt" : "pct";
    if (key === "depreciation_drivers")
      return fd.depreciation_method === "absolute" ? "amt" : "pct";
    return fd.capex_method === "ratio_to_sales" ? "pct" : "amt";   // capex 기본 금액
  };
  const setMode = (key: DriverKey, mode: "pct" | "amt") => {
    if (key === "cogs_ratio")
      setPayloadField("forecast_drivers.cogs_method", mode === "amt" ? "absolute" : "ratio");
    else if (key === "sga_drivers")
      setPayloadField("forecast_drivers.sga_method", mode === "amt" ? "absolute" : "ratio");
    else if (key === "depreciation_drivers") {
      if (fd.depreciation_method === "asset_register") return;   // 자산등록부가 우선
      setPayloadField("forecast_drivers.depreciation_method",
        mode === "amt" ? "absolute" : "ratio_to_sales");
    } else
      setPayloadField("forecast_drivers.capex_method",
        mode === "amt" ? "absolute" : "ratio_to_sales");
  };
  // 자산등록부를 쓰면 감가상각은 자산별 스케줄에서 나온다 — 드라이버 표의
  // %/금액 선택은 의미가 없어 잠근다(둘 다 열면 어느 쪽이 반영됐는지 알 수 없다).
  const depLocked = (key: DriverKey) =>
    (key === "depreciation_drivers" && fd.depreciation_method === "asset_register")
    // CAPEX 를 '기존 감가상각비 재투자'로 두면 금액이 감가상각비에서 나온다.
    // %/금액 선택을 열어두면 어느 쪽이 반영됐는지 알 수 없다.
    || (key === "capex_drivers" && fd.capex_method === "ratio_to_dep");

  /** 잠긴 이유 — 항목마다 다르므로 안내도 나눈다. */
  const lockTip = (key: DriverKey) =>
    key === "capex_drivers" ? t("tip.capex_locked") : t("tip.dep_locked");

  const commitDriver = (key: DriverKey, i: number, raw: string) => {
    const isPct = modeOf(key) === "pct";
    const arr = [...scenArr(fd, key, scen)];
    while (arr.length <= i) arr.push(0);
    arr[i] = isPct ? parseNum(raw) / 100 : parseNum(raw);
    const next: DcfForecastDrivers = {
      ...fd,
      scenario_choice: scen,
      [key]: withScenArr(fd[key] as ScenarioArray | undefined, scen, arr),
    };
    setPayloadField("forecast_drivers", next);
  };
  const driverCell = (key: DriverKey, i: number): string => {
    const v = scenArr(fd, key, scen)[i];
    if (v == null) return "";
    return modeOf(key) === "pct" ? (v * 100).toFixed(1) : Math.round(v).toLocaleString("ko-KR");
  };

  // ── 거시지표 연도별 (물가·임금·시장성장) ──
  // 리스트(macro.*_rates / market_growth_rates)가 있으면 그 값, 없으면 스칼라를
  // 전 연도에 표시한다. 한 칸이라도 고치면 리스트로 승격(엔진은 옵트인).
  type MacroRow = { labelKey: string; scalar: number; list?: number[] | null; path: string };
  const macro = (p.macro || {}) as {
    inflation_rate?: number; inflation_rates?: number[] | null;
    wage_growth_rate?: number; wage_growth_rates?: number[] | null;
  };
  const macroRows: MacroRow[] = [
    { labelKey: "row.inflation", scalar: macro.inflation_rate ?? 0.025,
      list: macro.inflation_rates, path: "macro.inflation_rates" },
    { labelKey: "row.wage_growth", scalar: macro.wage_growth_rate ?? 0.025,
      list: macro.wage_growth_rates, path: "macro.wage_growth_rates" },
    { labelKey: "row.market_growth", scalar: (p.market_growth_rate as number) ?? 0.075,
      list: p.market_growth_rates as number[] | null, path: "market_growth_rates" },
  ];
  const macroCell = (row: MacroRow, i: number): string => {
    const v = row.list?.[i] ?? row.scalar;
    return ((v ?? 0) * 100).toFixed(1);
  };
  const commitMacro = (row: MacroRow, i: number, raw: string) => {
    const arr = Array.from({ length: years }, (_, k) => row.list?.[k] ?? row.scalar);
    arr[i] = parseNum(raw) / 100;
    setPayloadField(row.path, arr);
  };

  // ── CAPEX 추정 방식 ──
  // 엔진(forecast.py)은 ratio_to_sales | ratio_to_dep | absolute 를 받는다.
  // 화면에는 리뷰 요청대로 둘만 둔다 — 매출비율은 CAPEX 드라이버 행의
  // '% (매출대비)' 로 이미 낼 수 있어 선택지가 겹쳤다.
  const capexMethod =
    fd.capex_method === "ratio_to_dep" ? "ratio_to_dep" : "absolute";
  const onCapexMethodChange = (v: string) => {
    setPayloadField("forecast_drivers.capex_method", v);
    // 재투자로 바꾸면 CAPEX 드라이버는 감가상각비에 대한 배수(1 = 100%)가 된다
    if (v === "ratio_to_dep") {
      setPayloadField("forecast_drivers.capex_drivers",
        Array.from({ length: years }, () => 1));
    }
  };

  // ── 항목별 CAPEX (capex_items) ──
  // 'CAPEX 100/200/300' 처럼 총액만 받으면 그 투자가 언제까지 상각되는지 알 수
  // 없어 재투자 가정을 세울 수 없다. 항목·내용연수를 함께 받아 둔다.
  // 지금은 연도별 합계만 capex_drivers 로 반영하고 내용연수는 보관만 한다.
  const capexItems: DcfCapexItem[] = (p.capex_items as DcfCapexItem[]) || [];
  const newCapexItem = (): DcfCapexItem => ({
    name: "", category: "tangible", useful_life_years: null,
    amounts: Array.from({ length: years }, () => 0),
  });
  /** 항목 합계를 CAPEX 드라이버에 반영 — 엔진은 capex_drivers 만 본다. */
  const syncCapexTotals = (rows: DcfCapexItem[]) => {
    setPayloadField("capex_items", rows);
    if (!rows.length) return;
    setPayloadField(
      "forecast_drivers.capex_drivers",
      Array.from({ length: years }, (_, i) =>
        rows.reduce((sum, r) => sum + (r.amounts?.[i] ?? 0), 0)),
    );
    setPayloadField("forecast_drivers.capex_method", "absolute");
  };
  const commitCapexItem = (i: number, key: string, raw: string) => {
    const rows = capexItems.map((r) => ({ ...r, amounts: [...(r.amounts || [])] }));
    if (!rows[i]) return;
    if (key === "name") rows[i].name = raw.trim();
    else if (key === "category") rows[i].category = raw;
    else if (key === "useful_life_years") rows[i].useful_life_years = parseNum(raw) || null;
    syncCapexTotals(rows);
  };
  const commitCapexAmount = (i: number, y: number, raw: string) => {
    const rows = capexItems.map((r) => ({ ...r, amounts: [...(r.amounts || [])] }));
    if (!rows[i]) return;
    const arr = rows[i].amounts as number[];
    while (arr.length <= y) arr.push(0);
    arr[y] = parseNum(raw);
    syncCapexTotals(rows);
  };
  const addCapexItem = () =>
    syncCapexTotals([...capexItems.map((r) => ({ ...r })), newCapexItem()]);
  const delCapexItem = (i: number) => {
    pushUndo("capex_items", capexItems.map((r) => ({ ...r, amounts: [...(r.amounts || [])] })), capexItems[i]?.name || "CapEx");
    const rows = capexItems.filter((_, k) => k !== i).map((r) => ({ ...r }));
    syncCapexTotals(rows);
    // 항목을 다 지우면 드라이버 표에서 다시 직접 입력하는 상태로 돌아간다
    if (!rows.length) setPayloadField("capex_items", []);
  };

  // ── (유무형)자산명세서 (asset_register) ──
  const depMethod =
    fd.depreciation_method === "asset_register" ? "asset_register" : "ratio_to_sales";
  const fas: DcfFixedAsset[] = p.fixed_assets || [];
  const defaultFa = (): DcfFixedAsset => ({
    name: "",
    category: "tangible",
    gross_cost: 0,
    accumulated_depreciation: 0,
    remaining_useful_life_years: 5,
    useful_life_years: 5,
  });
  const onDepMethodChange = (v: string) => {
    if (v === "asset_register") {
      setPayloadField("forecast_drivers.depreciation_method", "asset_register");
      if (!fas.length) setPayloadField("fixed_assets", [defaultFa()]);
    } else {
      // 바닐라 readForm else 분기 — 자산등록부 해제 시 목록 비움
      setPayloadField("forecast_drivers.depreciation_method", "ratio_to_sales");
      setPayloadField("fixed_assets", []);
    }
  };
  const commitFa = (i: number, key: keyof DcfFixedAsset, raw: string) => {
    const rows = fas.map((row) => ({ ...row }));
    if (!rows[i]) return;
    if (key === "name") rows[i].name = raw.trim();
    else if (key === "category") rows[i].category = raw;
    else if (key === "useful_life_years") rows[i].useful_life_years = parseNum(raw) || null;
    else rows[i][key] = parseNum(raw);
    setPayloadField("fixed_assets", rows);
  };
  const addFaRow = () =>
    setPayloadField("fixed_assets", [...fas.map((row) => ({ ...row })), defaultFa()]);
  const delFaRow = (i: number) => {
    pushUndo("fixed_assets", fas.map((row) => ({ ...row })), fas[i]?.name || "자산");
    setPayloadField(
      "fixed_assets",
      fas.filter((_, idx) => idx !== i).map((row) => ({ ...row })),
    );
  };

  // ── 운전자본 (NWC) ──
  const nwcMethod = fd.nwc_method || "turnover_days";
  type NwcKey = "ar_days" | "inv_days" | "ap_days" | "nwc_to_sales";
  const commitNwc = (key: NwcKey, i: number, raw: string) => {
    const isPct = key === "nwc_to_sales";
    const arr = [...scenArr(fd, key, scen)];
    while (arr.length <= i) arr.push(0);
    arr[i] = isPct ? parseNum(raw) / 100 : parseNum(raw);
    const next: DcfForecastDrivers = {
      ...fd,
      nwc_method: nwcMethod,
      [key]: withScenArr(fd[key] as ScenarioArray | undefined, scen, arr),
    };
    setPayloadField("forecast_drivers", next);
  };
  // 과거 재무제표에서 회전기일을 계산해 채운다 (사용자 요청 — 업로드 자료에
  // 회전기일이 없을 때 손으로 셈하지 않게). 최근 연도 말잔 기준:
  //   AR = 매출채권/매출×365 · 재고 = 재고/원가×365 · AP = 매입채무/원가×365
  // (원가가 0이면 매출 기준 — 엔진 NWC 시트의 IF 폴백과 같은 규칙)
  const histDaysAvailable = (() => {
    const bs = (p.historical_bs || [])[ (p.historical_bs || []).length - 1];
    const pl = hist[hist.length - 1];
    return !!(bs && pl && pl.revenue);
  })();
  const fillDaysFromHistory = () => {
    const bs = (p.historical_bs || [])[(p.historical_bs || []).length - 1];
    const pl = hist[hist.length - 1];
    if (!bs || !pl || !pl.revenue) return;
    const rev = Number(pl.revenue) || 0;
    const cogs = Number(pl.cogs) || 0;
    const base = cogs > 0 ? cogs : rev;
    const day = (bal: unknown, drv: number) =>
      drv > 0 ? Math.round(((Number(bal) || 0) / drv) * 365 * 10) / 10 : 0;
    const ar = day(bs.accounts_receivable, rev);
    const inv = day(bs.inventory, base);
    const ap = day(bs.accounts_payable, base);
    const next: DcfForecastDrivers = {
      ...fd,
      nwc_method: "turnover_days",
      ar_days: withScenArr(fd.ar_days as ScenarioArray | undefined, scen, Array(years).fill(ar)),
      inv_days: withScenArr(fd.inv_days as ScenarioArray | undefined, scen, Array(years).fill(inv)),
      ap_days: withScenArr(fd.ap_days as ScenarioArray | undefined, scen, Array(years).fill(ap)),
    };
    setPayloadField("forecast_drivers", next);
  };

  const nwcCell = (key: NwcKey, i: number): string => {
    const v = scenArr(fd, key, scen)[i];
    if (v == null) return "";
    return key === "nwc_to_sales" ? (v * 100).toFixed(2) : String(v);
  };

  // ── 추정 결과 (엔진 산출) — 바닐라 renderResult 의 forecast-out ──
  const labels =
    r?.meta?.period_labels || [1, 2, 3, 4, 5].map((n) => yearCol(lang, n));
  const fc = r?.forecast || {};
  const ff = r?.fcff || {};
  const ts = r?.tax_schedule || {};
  const taxes =
    ts.rows && ts.rows.length
      ? ts.rows.map((x) => x.tax)
      : (ff.ebit || []).map((e) => Math.max(0, e) * (ff.tax_rate || 0));
  const taxLabel = `${t("out.tax")} (${((ff.tax_rate || 0) * 100).toFixed(1)}%)`;
  const m = unitMoney(lang);
  const OutRow = ({
    label,
    arr,
    total,
  }: {
    label: string;
    arr?: Array<number | undefined>;
    total?: boolean;
  }) => (
    <tr className={total ? "total" : undefined}>
      <td>{label}</td>
      {(arr || []).map((v, i) => (
        <td key={i}>{fmt(v)}</td>
      ))}
    </tr>
  );

  // ── 자산별 감가상각·CapEx 스케줄 — 바닐라 renderFaSchedule (KR) ──
  const as = fc.asset_schedule;
  const hasSchedule = !!(as && as.items && as.items.length);

  const yearHead = (
    <thead>
      <tr>
        <th>{t("col.item")}</th>
        {YEARS_5.map((i) => (
          <th key={i}>{yearCol(lang, i + 1)}</th>
        ))}
      </tr>
    </thead>
  );

  return (
    <>
      {undoCount > 0 && (
        <button type="button" className="undo-pill" onClick={undoLast}
                title="Ctrl+Z">
          ↩ {tf("btn.undo_del", { name: lastDeleted })}
        </button>
      )}
      <div className="data-card">
        <h4>
          {t("h.historical_pl")}{" "}
          <span className="hint">{tf("hint.hist_unit", { m })}</span>
        </h4>
        {/* transpose — 컬럼=연도, 로우=계정. 엑셀에서 보던 방향과 같다 (사용자 피드백) */}
        <div style={{ overflowX: "auto" }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ minWidth: 110 }}>{t("col.item")}</th>
              {hist.map((row, i) => (
                <th key={i} style={{ minWidth: 110 }}>
                  <span className="hist-year" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Cell
                      value={row.year_end || ""}
                      onCommit={(v) => commitHist(i, "year_end", v)}
                      onPaste={onHistPasteT(0, i)}
                    />
                    <Button
                      variant="del"
                      title={t("btn.del_row")}
                      onClick={() => removeHistRow(i)}
                    >
                      ×
                    </Button>
                  </span>
                </th>
              ))}
              {!hist.length && <th className="hint">{t("hist.empty")}</th>}
            </tr>
          </thead>
          <tbody>
            {(
              [
                // derived 는 입력이 아니라 계산 행 — 엑셀 템플릿의 매출총이익·영업이익
                // 줄과 같은 자리에 같은 값이 보이게 한다 (사용자 요청)
                ["col.revenue", "revenue", 0],
                ["col.cogs", "cogs", 1],
                ["col.gross", "__gross", -1],
                ["col.sga", "sga", 2],
                ["col.dep", "depreciation", 3],
                ["col.op", "__op", -1],
              ] as Array<[string, string, number]>
            ).map(([labelKey, field, pasteRow]) => {
              if (field.startsWith("__")) {
                // 감가상각은 판관비에 포함(엑셀 각주와 동일) — 영업이익에서 다시 빼지 않는다
                const calc = (row: DcfHistRow) => {
                  const rev = row.revenue, cogs = row.cogs, sga = row.sga;
                  if (rev == null && cogs == null && sga == null) return null;
                  const gross = (rev || 0) - (cogs || 0);
                  return field === "__gross" ? gross : gross - (sga || 0);
                };
                return (
                  <tr key={field} className="hist-derived">
                    <td>{t(labelKey)}</td>
                    {hist.map((row, i) => {
                      const v = calc(row);
                      return <td key={i} className="num">{v != null ? Math.round(v).toLocaleString("ko-KR") : ""}</td>;
                    })}
                  </tr>
                );
              }
              const f = field as "revenue" | "cogs" | "sga" | "depreciation";
              return (
              <tr key={field}>
                <td>
                  {t(labelKey)}
                  {f === "depreciation" && (
                    <span className="info-tip" tabIndex={0} aria-label={t("tip.dep")} title={t("tip.dep")}>i</span>
                  )}
                </td>
                {hist.map((row, i) => (
                  <td key={i}>
                    <Cell
                      value={row[f] != null ? Math.round(row[f] as number).toLocaleString("ko-KR") : ""}
                      onCommit={(v) => commitHist(i, f, v)}
                      onPaste={onHistPasteT(pasteRow + 1, i)}
                    />
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <div style={{ marginTop: 10 }}>
          <Button variant="default" onClick={addHistRow}>
            {t("btn.add_hist")}
          </Button>
        </div>
      </div>

      <div className="data-card">
        <h4>
          {t("h.historical_bs")}{" "}
          <span className="hint">{tf("hint.hist_unit", { m })}</span>
        </h4>
        <p className="hint" style={{ margin: "0 0 8px" }}>{t("hint.historical_bs")}</p>
        {bsMissing && (
          <p className="lab-error" role="note" style={{ margin: "0 0 8px" }}>
            {t("warn.bs_missing")}
          </p>
        )}
        <div style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ minWidth: 110 }}>{t("col.item")}</th>
                {histBs.map((row, i) => (
                  <th key={i} style={{ minWidth: 110 }}>
                    <span className="hist-year" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Cell value={row.year_end || ""}
                            onCommit={(v) => commitBs(i, "year_end", v)} />
                      <Button variant="del" title={t("btn.del_row")}
                              onClick={() => delBsRow(i)}>×</Button>
                    </span>
                  </th>
                ))}
                {!histBs.length && <th className="hint">{t("bs.empty")}</th>}
              </tr>
            </thead>
            <tbody>
              {BS_FIELDS.map(([labelKey, field]) => (
                <tr key={String(field)}>
                  <td>{t(labelKey)}</td>
                  {histBs.map((row, i) => (
                    <td key={i}>
                      <Cell
                        value={row[field] ? Math.round(row[field] as number).toLocaleString("ko-KR") : ""}
                        onCommit={(v) => commitBs(i, field, v)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              {/* 입력이 실제로 얼마가 되는지 그 자리에서 보여준다 — 직전 NWC 는
                  마지막 연도 값이 쓰인다 */}
              {histBs.length > 0 && (
                <tr>
                  <td><strong>{t("row.bs_nwc")}</strong></td>
                  {histBs.map((row, i) => (
                    <td key={i}><strong>{Math.round(bsNwc(row)).toLocaleString("ko-KR")}</strong></td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <Button variant="default" onClick={addBsRow}>{t("btn.add_bs")}</Button>
          {hist.some((h) => h.year_end) && (
            <Button variant="default" onClick={pullBsYears}>{t("btn.pull_bs_years")}</Button>
          )}
        </div>
      </div>

      <div className="data-card">
        <h4>{t("h.forecast_assumptions")}</h4>
        <div className="wacc-row">
          <span className="wacc-label">{t("lbl.scenario")}</span>
          <select
            className={`wacc-input${flashOf("forecast_drivers.scenario_choice") ? " cell-flash" : ""}`}
            style={{ width: 200 }}
            value={scen}
            onChange={(e) =>
              setPayloadField("forecast_drivers.scenario_choice", e.target.value)
            }
          >
            <option value="business_plan">{t("opt.scen_bp")}</option>
            <option value="historical">{t("opt.scen_hist")}</option>
            <option value="market">{t("opt.scen_mkt")}</option>
          </select>
        </div>

      </div>

      <div className="data-card">
        <h4>
          {t("h.driver_input")}{" "}
          <span className="hint">{tf("hint.driver_unit", { m })}</span>
        </h4>
        <table className="data-table">
          {yearHead}
          <tbody>
            <tr>
              <td>
                <span className="drv-label">
                  {t("col.revenue")}
                  <select
                    className="drv-mode"
                    value={revMode}
                    aria-label={t("lbl.rev_mode")}
                    onChange={(e) => {
                      const md = e.target.value as "amounts" | "growth";
                      setRevMode(md);
                      setPayloadField("forecast_drivers.revenue_input_method", md);
                    }}
                  >
                    <option value="amounts">{tf("opt.mode_amt", { m })}</option>
                    <option value="growth">{t("opt.rev_growth")}</option>
                  </select>
                </span>
              </td>
              {YEARS_5.map((i) => (
                <td key={i} style={i >= years ? { opacity: 0.25 } : undefined}>
                  <Cell
                    value={revCell(i)}
                    disabled={i >= years}
                    flash={revFlash}
                    flashSeq={patchSeq}
                    onCommit={(v) => commitRev(i, v)}
                  />
                </td>
              ))}
            </tr>
            {(
              [
                ["col.cogs", "cogs_ratio"],
                ["col.sga", "sga_drivers"],
                ["col.dep", "depreciation_drivers"],
                ["row.capex_name", "capex_drivers"],
              ] as Array<[string, DriverKey]>
            ).map(([labelKey, key]) => (
              <tr key={key}>
                <td>
                  <span className="drv-label">
                    {t(labelKey)}
                    <select
                      className="drv-mode"
                      value={modeOf(key)}
                      disabled={depLocked(key)}
                      aria-label={t("lbl.drv_mode")}
                      onChange={(e) => setMode(key, e.target.value as "pct" | "amt")}
                    >
                      <option value="pct">{t("opt.mode_pct")}</option>
                      <option value="amt">{tf("opt.mode_amt", { m })}</option>
                    </select>
                    {/* 왜 잠겼는지 화면에 단서가 없으면 사용자는 고장으로 읽는다 */}
                    {depLocked(key) && (
                      <span className="drv-why" tabIndex={0} role="note"
                            aria-label={lockTip(key)} title={lockTip(key)}>
                        i
                      </span>
                    )}
                  </span>
                </td>
                {YEARS_5.map((i) => (
                  <td key={i} style={i >= years ? { opacity: 0.25 } : undefined}>
                    <Cell
                      value={driverCell(key, i)}
                      disabled={i >= years}
                      flash={flashOf(`forecast_drivers.${key}`)}
                      flashSeq={patchSeq}
                      onCommit={(v) => commitDriver(key, i, v)}
                    />
                  </td>
                ))}
              </tr>
            ))}
            {macroRows.map((row) => (
              <tr key={row.path}>
                <td>
                  {/* 위 행들은 금액/비율 드롭다운이 붙어 있는데 거시지표만 아무
                      표시가 없어, 이 값이 어떻게 정해지는지 알 수 없었다.
                      선택지가 없는 항목이므로 드롭다운이 아니라 고정 배지다. */}
                  <span className="drv-label">
                    {t(row.labelKey)}
                    <span className="drv-fixed">{t("opt.mode_manual")}</span>
                  </span>
                </td>
                {YEARS_5.map((i) => (
                  <td key={i} style={i >= years ? { opacity: 0.25 } : undefined}>
                    <Cell
                      value={macroCell(row, i)}
                      disabled={i >= years}
                      flash={flashOf(row.path)}
                      flashSeq={patchSeq}
                      onCommit={(v) => commitMacro(row, i, v)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 14 }}>
          <Button variant="primary" onClick={() => void recalc()}>
            {t("btn.driver_recalc")}
          </Button>
        </div>
      </div>

      <div className="data-card">
        <h4>
          {t("h.dep_capex")} <span className="hint">{t("hint.dep_capex")}</span>
        </h4>
        <div className="wacc-row">
          <span className="wacc-label">{t("lbl.dep_method")}</span>
          <select
            className="wacc-input"
            style={{ width: 260 }}
            value={depMethod}
            onChange={(e) => onDepMethodChange(e.target.value)}
          >
            <option value="ratio_to_sales">{t("opt.dep_ratio")}</option>
            <option value="asset_register">{t("opt.dep_register")}</option>
          </select>
        </div>
        {/* CAPEX 직접입력이면 항목·내용연수를 함께 받는다. 드라이버 표의 한 줄로는
            '무엇에 얼마를 언제까지' 를 담을 수 없어 재투자 가정을 못 세운다. */}
        {depMethod === "ratio_to_sales" && capexMethod === "absolute" && (
          <div style={{ marginTop: 12 }}>
            <p className="hint" style={{ margin: "0 0 6px" }}>
              {t("hint.capex_items")} · {t("hint.fa_unit")}
            </p>
            {capexItems.length > 0 && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 130 }}>{t("col.capex_name")}</th>
                    <th>{t("col.fa_cat")}</th>
                    <th>{t("col.capex_life")}</th>
                    {YEARS_5.slice(0, years).map((i) => (
                      <th key={i}>{yearCol(lang, i + 1)}</th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {capexItems.map((it, i) => (
                    <tr key={i}>
                      <td>
                        <Cell left value={it.name || ""}
                              onCommit={(v) => commitCapexItem(i, "name", v)} />
                      </td>
                      <td>
                        <select className="proj-input" value={it.category || "tangible"}
                                onChange={(e) => commitCapexItem(i, "category", e.target.value)}>
                          {FA_CATS.map(([val, labelKey]) => (
                            <option key={val} value={val}>{t(labelKey)}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <Cell value={it.useful_life_years != null ? String(it.useful_life_years) : ""}
                              onCommit={(v) => commitCapexItem(i, "useful_life_years", v)} />
                      </td>
                      {YEARS_5.slice(0, years).map((y) => (
                        <td key={y}>
                          <Cell value={it.amounts?.[y] ? Math.round(it.amounts[y]).toLocaleString("ko-KR") : ""}
                                onCommit={(v) => commitCapexAmount(i, y, v)} />
                        </td>
                      ))}
                      <td>
                        <Button variant="del" title={t("btn.del_row")}
                                onClick={() => delCapexItem(i)}>×</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <Button onClick={addCapexItem} style={{ marginTop: 8 }}>
              {t("btn.add_capex_item")}
            </Button>
          </div>
        )}
        {/* 자산명세서를 쓰면 CAPEX 도 자산별 재투자 스케줄에서 나오므로 감춘다 */}
        {depMethod === "ratio_to_sales" && (
          <div className="wacc-row">
            <span className="wacc-label">{t("lbl.capex_method")}</span>
            <select
              className="wacc-input"
              style={{ width: 260 }}
              value={capexMethod}
              onChange={(e) => onCapexMethodChange(e.target.value)}
            >
              <option value="ratio_to_dep">{t("opt.capex_reinvest")}</option>
              <option value="absolute">{t("opt.capex_manual")}</option>
            </select>
            {capexMethod === "ratio_to_dep" && (
              <span className="hint">{t("hint.capex_reinvest")}</span>
            )}
          </div>
        )}
        {depMethod === "asset_register" && (
          <div style={{ marginTop: 10 }}>
            <p className="hint" style={{ margin: "0 0 6px" }}>{t("hint.fa_unit")}</p>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 130 }}>{t("col.fa_name")}</th>
                  <th>{t("col.fa_cat")}</th>
                  <th>{t("col.fa_cost")}</th>
                  <th>{t("col.fa_accdep")}</th>
                  <th>{t("col.fa_remyears")}</th>
                  <th>{t("col.fa_life")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {fas.length ? (
                  fas.map((f, i) => (
                    <tr key={i}>
                      <td>
                        <Cell
                          left
                          value={f.name || ""}
                          onCommit={(v) => commitFa(i, "name", v)}
                        />
                      </td>
                      <td>
                        <select
                          className="proj-input"
                          value={f.category || "tangible"}
                          onChange={(e) => commitFa(i, "category", e.target.value)}
                        >
                          {FA_CATS.map(([val, labelKey]) => (
                            <option key={val} value={val}>
                              {t(labelKey)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <Cell
                          value={
                            f.gross_cost != null
                              ? Math.round(f.gross_cost).toLocaleString("ko-KR")
                              : ""
                          }
                          onCommit={(v) => commitFa(i, "gross_cost", v)}
                        />
                      </td>
                      <td>
                        <Cell
                          value={
                            f.accumulated_depreciation != null
                              ? Math.round(f.accumulated_depreciation).toLocaleString("ko-KR")
                              : ""
                          }
                          onCommit={(v) => commitFa(i, "accumulated_depreciation", v)}
                        />
                      </td>
                      <td>
                        <Cell
                          value={
                            f.remaining_useful_life_years != null
                              ? String(f.remaining_useful_life_years)
                              : ""
                          }
                          onCommit={(v) => commitFa(i, "remaining_useful_life_years", v)}
                        />
                      </td>
                      <td>
                        <Cell
                          value={f.useful_life_years != null ? String(f.useful_life_years) : ""}
                          onCommit={(v) => commitFa(i, "useful_life_years", v)}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          variant="del"
                          title={t("btn.del_row")}
                          onClick={() => delFaRow(i)}
                        >
                          ✕
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="hint" colSpan={7}>
                      {t("fa.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop: 8 }}>
              <Button variant="default" onClick={addFaRow}>
                {t("btn.add_fa")}
              </Button>
            </div>
            <p className="hint" style={{ marginTop: 8 }}>
              {t("hint.fa_reinvest")}
            </p>
          </div>
        )}
      </div>

      <div className="data-card">
        <h4>{t("h.nwc_method")}</h4>
        <div className="wacc-row">
          <span className="wacc-label">{t("lbl.nwc_calc")}</span>
          <select
            className={`wacc-input${flashOf("forecast_drivers.nwc_method") ? " cell-flash" : ""}`}
            style={{ width: 260 }}
            value={nwcMethod}
            onChange={(e) => setPayloadField("forecast_drivers.nwc_method", e.target.value)}
          >
            <option value="turnover_days">{t("opt.nwc_days")}</option>
            <option value="ratio_to_sales">{t("opt.nwc_ratio")}</option>
          </select>
        </div>
        {nwcMethod === "turnover_days" && histDaysAvailable && (
          <div style={{ marginTop: 10 }}>
            <Button variant="default" onClick={fillDaysFromHistory}>
              {t("btn.days_from_hist")}
            </Button>
            <span className="hint" style={{ marginLeft: 8 }}>{t("hint.days_from_hist")}</span>
          </div>
        )}
        {nwcMethod === "turnover_days" ? (
          <table className="data-table" style={{ marginTop: 10 }}>
            {yearHead}
            <tbody>
              {(
                [
                  ["row.ar_days", "ar_days"],
                  ["row.inv_days", "inv_days"],
                  ["row.ap_days", "ap_days"],
                ] as Array<[string, NwcKey]>
              ).map(([labelKey, key]) => (
                <tr key={key}>
                  <td>{t(labelKey)}</td>
                  {YEARS_5.map((i) => (
                    <td key={i} style={i >= years ? { opacity: 0.25 } : undefined}>
                      <Cell
                        value={nwcCell(key, i)}
                        disabled={i >= years}
                        flash={flashOf(`forecast_drivers.${key}`)}
                        flashSeq={patchSeq}
                        onCommit={(v) => commitNwc(key, i, v)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="data-table" style={{ marginTop: 10 }}>
            {yearHead}
            <tbody>
              <tr>
                <td>{t("row.nwc_ratio")}</td>
                {YEARS_5.map((i) => (
                  <td key={i} style={i >= years ? { opacity: 0.25 } : undefined}>
                    <Cell
                      value={nwcCell("nwc_to_sales", i)}
                      disabled={i >= years}
                      flash={flashOf("forecast_drivers.nwc_to_sales")}
                      flashSeq={patchSeq}
                      onCommit={(v) => commitNwc("nwc_to_sales", i, v)}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div className="data-card">
        <h4>{t("h.forecast_output")}</h4>
        {r ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("col.item")}</th>
                {labels.map((l) => (
                  <th key={l}>{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <OutRow label={t("col.revenue")} arr={fc.revenue} />
              <OutRow label={t("col.cogs")} arr={fc.cogs} />
              <OutRow label={t("col.sga")} arr={fc.sga} />
              <OutRow label={t("out.ebit")} arr={fc.ebit} total />
              <OutRow label={"(-)" + taxLabel} arr={taxes} />
              <OutRow label="NOPLAT" arr={ff.noplat} total />
              <OutRow label={t("out.dep_add")} arr={fc.depreciation} />
              <OutRow label={t("out.capex")} arr={fc.capex} />
              <OutRow label={t("out.nwc")} arr={ff.nwc_change} />
              <OutRow label="FCFF" arr={ff.fcff} total />
            </tbody>
          </table>
        ) : (
          <p className="hint">{t("ph.recalc_show")}</p>
        )}
      </div>

      {hasSchedule && as && (
        <div className="data-card">
          <h4>
            {t("h.fa_schedule")} <span className="hint">{t("hint.fa_schedule")}</span>
          </h4>
          {(
            [
              [t("lbl.dep_cost"), "depreciation"],
              ["CapEx", "capex"],
            ] as Array<[string, "depreciation" | "capex"]>
          ).map(([label, key]) => (
            <div key={key} style={{ marginBottom: key === "depreciation" ? 20 : 0 }}>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--text-secondary)",
                  margin: "0 0 6px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                {label} ({m})
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 120 }}>{t("col.asset_name")}</th>
                      <th style={{ minWidth: 60 }}>{t("col.fa_cat")}</th>
                      {labels.map((l) => (
                        <th key={l}>{l}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(as.items || []).map((it, idx) => (
                      <tr key={idx}>
                        <td>{it.name}</td>
                        <td style={{ color: "var(--text-secondary)" }}>
                          {t(faCatKey(it.category))}
                        </td>
                        {(it[key] || []).map((v, i) => {
                          // 셀 위에 계산 근거 — 숫자만 보이면 재투자 CapEx 가
                          // 어디서 왔는지 알 수 없다 (사용자 요청: 호버 팝업)
                          const ex = it.existing_depreciation?.[i] ?? 0;
                          const tip = key === "capex"
                            ? tf("tip.fa_capex", { a: fmt(ex), b: fmt(v - ex) })
                            : tf("tip.fa_dep", {
                                a: fmt(ex),
                                b: fmt(it.reinvest_depreciation?.[i] ?? 0),
                                c: fmt(it.new_depreciation?.[i] ?? 0),
                              });
                          return (
                            <td key={i}><span className="calc-tip" title={tip}>{fmt(v)}</span></td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr className="total">
                      <td>{t("row.total")}</td>
                      <td />
                      {(as[key] || []).map((v, i) => (
                        <td key={i}>{fmt(v)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
